import { UpOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { Button, Col, FloatButton, Pagination, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import {
  BlobFinanceConfigInput,
  StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment,
  StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment,
  StaffFinanceSettingsContainerStaffUserFieldsFragment
} from '../../../../generated';
import { FinanceSettingsAdd } from './finance-settings-add';
import { FinanceSettingsEdit } from './finance-settings-edit';
import { FinanceSettingsList } from './finance-settings-list';
import { ApplicationTypeEnum } from './finance-settings.enum';

interface FinanceSettingsProps {
  data: StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment[];
  staffUsers: StaffFinanceSettingsContainerStaffUserFieldsFragment[];
  onSave: (input: BlobFinanceConfigInput) => Promise<void>;
  onRowClick: (id: string) => Promise<StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment>;
}

export const FinanceSettings: React.FC<FinanceSettingsProps> = (props) => {
  const [activeTab, setActiveTab] = useState('id-case');
  const [selectedRecord, setSelectedRecord] = useState<StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);

  // Create a ref for the form
  const formRef = useRef<HTMLDivElement | null>(null);
  const recordsPerPage = 5;

  // Function to handle row selection from the table
  const handleRowSelection = useCallback(
    async (fieldKey: string | null) => {
      setIsAddFormVisible(false);
      if (fieldKey) {
        const result = await props.onRowClick(fieldKey);
        console.log('result: ', result);
        setSelectedRecord(result);

        // Scroll to the form when a row is selected
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    },
    [props]
  );

  const handlePaginationChange = (page: SetStateAction<number>) => {
    setCurrentPage(page);
  };

  const handleCancelForm = () => {
    setSelectedRecord(null);
    setIsAddFormVisible(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSelectedRecord(null);
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddNewClick = () => {
    setIsAddFormVisible(true);
    setSelectedRecord(null);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const filteredData = useMemo(() => props.data.filter((record) => record.caseType?.includes(activeTab)), [props.data, activeTab]);

  return (
    <>
      <PageHeader>
        <div id="leftTitle" style={{ marginBottom: '30px' }}>
          <Title level={3} style={{ display: 'inline' }}>
            Finance Settings
          </Title>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <Title level={4} type="secondary" style={{ display: 'inline' }}>
            Charges / Distribution Rules
          </Title>
        </div>
      </PageHeader>
      <FinanceSettingsList
        data={props.data}
        selectedRecord={selectedRecord}
        currentPage={currentPage}
        activeTab={activeTab as ApplicationTypeEnum}
        onTabChange={handleTabChange}
        onRowSelect={handleRowSelection}
      />
      <div ref={formRef} />
      <Row justify="space-between" style={{ padding: '10px 0' }}>
        <Col>
          <Button type="primary" onClick={handleAddNewClick}>
            Add New
          </Button>
        </Col>
        <Col>
          <Pagination current={currentPage} total={filteredData.length} pageSize={recordsPerPage} onChange={handlePaginationChange} />
        </Col>
      </Row>
      {isAddFormVisible && <FinanceSettingsAdd financialSettingsList={props.data} onSave={props.onSave} onCancel={handleCancelForm} />}
      {selectedRecord && (
        <FinanceSettingsEdit
          data={selectedRecord}
          financialSettingsList={props.data}
          staffData={props.staffUsers}
          onSave={props.onSave}
          onCancel={handleCancelForm}
          activeCaseType={activeTab}
        />
      )}
      <FloatButton icon={<UpOutlined />} onClick={scrollToTop} />
    </>
  );
};
